import { copy } from '@mybonus/public';
import {
	Eyebrow,
	ErrorMessage,
	Heading,
	NumberedStep,
	PageContainer,
	PageTitle,
	Paragraph,
	TextButton,
	Waiter,
	useHead,
} from '@mybonus/ui';
import type { ReactNode } from 'react';

import {
	ContentBox,
	GateAction,
	GateBankIdButton,
	GateDescription,
	GateHeader,
	GatePanel,
	GateStage,
	GateStepText,
	GateSteps,
	Iframe,
	IframeWrapper,
	StageInset,
	IdentificationGateWrapper,
} from './MychipsView.parts';
import { useMychipsView } from './MychipsView.hooks';

const title = 'Erbjudanden';

export function MychipsView() {
	useHead({ subtitle: title });
	const { availability, launch, launchData, handleIdentified, handleRetry } = useMychipsView();
	const { offerwallGate, offerwallUnavailable } = copy;

	function renderOfferwallStage(args: {
		eyebrow: string;
		title: string;
		description: string;
		children?: ReactNode;
	}) {
		return (
			<StageInset>
				<GateStage>
					<GatePanel>
						<>
							<GateHeader>
								<Eyebrow>{args.eyebrow}</Eyebrow>
								<Heading level={2}>{args.title}</Heading>
								<GateDescription>{args.description}</GateDescription>
							</GateHeader>
							{args.children}
						</>
					</GatePanel>
				</GateStage>
			</StageInset>
		);
	}

	function renderIdentificationGate() {
		return renderOfferwallStage({
			eyebrow: offerwallGate.eyebrow,
			title: offerwallGate.title,
			description: offerwallGate.description,
			children: (
				<IdentificationGateWrapper>
					<GateSteps variant="card">
						{offerwallGate.steps.map((step) => (
							<NumberedStep key={step.title} heading={step.title}>
								<GateStepText>{step.text}</GateStepText>
							</NumberedStep>
						))}
					</GateSteps>
					<GateAction>
						<GateBankIdButton onIdentified={handleIdentified} reason={offerwallGate.ctaReason} />
					</GateAction>
				</IdentificationGateWrapper>
			),
		});
	}

	function renderUnavailableState() {
		return renderOfferwallStage({
			eyebrow: offerwallUnavailable.eyebrow,
			title: offerwallUnavailable.title,
			description: offerwallUnavailable.description,
		});
	}

	function renderBody() {
		if (availability.data?.status === 'LOGIN_REQUIRED') {
			return <Paragraph>Du skickas vidare till inloggningen.</Paragraph>;
		}

		if (availability.data?.status === 'IDENTIFICATION_REQUIRED') {
			return renderIdentificationGate();
		}

		if (
			availability.data?.status === 'UNAVAILABLE' ||
			availability.data?.status === 'MISSING_PROVIDER_USER_ID'
		) {
			return renderUnavailableState();
		}

		if (launch.error) {
			return (
				<>
					<ErrorMessage error={launch.error} />
					<TextButton variant="primary" text="Försök igen" onClick={handleRetry} />
				</>
			);
		}

		if (launchData?.iframeUrl) {
			return (
				<IframeWrapper>
					<Iframe src={launchData.iframeUrl} allow="clipboard-write" />
				</IframeWrapper>
			);
		}

		return null;
	}

	return (
		<PageContainer noTopSpace>
			<PageTitle>
				<Heading level={1}>{title}</Heading>
			</PageTitle>

			<ContentBox>
				<Waiter isLoading={availability.isLoading || launch.isLoading}>{renderBody()}</Waiter>
			</ContentBox>
		</PageContainer>
	);
}
